import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faClock, faPlayCircle, faSyncAlt, faUserGroup } from "@fortawesome/free-solid-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import JitsiMeetingEmbed from "./JitsiMeetingEmbed";
import TelehealthShell from "./TelehealthShell";
import { buildApiUrl, getJsonAuthHeaders, getStaffDetails, useBaseUrl } from "../../../utils/utils";

interface SessionDetails {
    id: string;
    session_id: string;
    title: string;
    description?: string;
    session_type: string;
    status: string;
    scheduled_time: string;
    duration: number;
    recording_enabled: boolean;
    participants_count: number;
    created_by: string;
    collaboration_id: string;
    collaboration_title: string;
    patient_name: string;
    notes?: string;
    room_name: string;
}

const TelehealthSessionPage: React.FC = () => {
    const { sessionId } = useParams<{ sessionId: string }>();
    const navigate = useNavigate();
    const baseUrl = useBaseUrl();

    const [session, setSession] = useState<SessionDetails | null>(null);
    const [loading, setLoading] = useState(true);
    const [displayName, setDisplayName] = useState("Clinic Host");
    const [email, setEmail] = useState("");

    useEffect(() => {
        document.title = "Live Telehealth Session - Uzima Healthcare";
        initialize();
    }, [sessionId]);

    const initialize = async () => {
        if (!sessionId) return;
        try {
            setLoading(true);
            const headers = await getJsonAuthHeaders();
            const [sessionResponse, staffDetails] = await Promise.all([
                fetch(buildApiUrl(baseUrl, `collaborations/telehealth-sessions/?session_id=${sessionId}`), { headers }),
                getStaffDetails(),
            ]);
            const sessionData = await sessionResponse.json();
            const resolvedSession = sessionData?.session || sessionData?.data?.session || null;
            if (!sessionResponse.ok || !resolvedSession) {
                throw new Error(sessionData?.message || "Unable to load telehealth session");
            }
            setSession(resolvedSession);
            setDisplayName(staffDetails ? `${staffDetails.fname || ""} ${staffDetails.lname || ""}`.trim() || staffDetails.username || "Clinic Host" : "Clinic Host");
            setEmail(staffDetails?.email || "");

            await fetch(buildApiUrl(baseUrl, `collaborations/telehealth-sessions/${sessionId}/join/`), {
                method: "POST",
                headers,
                body: JSON.stringify({ user_type: "admin", role: "host" }),
            });
        } catch (error: any) {
            console.error("Telehealth session initialization error:", error);
            Swal.fire({ icon: "error", title: "Session unavailable", text: error?.message || "Unable to initialize the telehealth session." }).then(() => {
                navigate("/institution/telehealth");
            });
        } finally {
            setLoading(false);
        }
    };

    const handleLeave = () => {
        navigate("/institution/telehealth");
    };

    return (
        <TelehealthShell
            title={session?.title || "Telehealth Session"}
            description={session ? `${session.patient_name} · ${session.collaboration_title}` : "Preparing live consultation room"}
            activeTab="overview"
            action={
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <button className="sc-btn sc-btn-outline" onClick={() => navigate("/institution/telehealth")}>
                        <FontAwesomeIcon icon={faArrowLeft} /> Back to center
                    </button>
                    <button className="sc-btn sc-btn-primary" onClick={initialize}>
                        <FontAwesomeIcon icon={faSyncAlt} /> Reload room
                    </button>
                </div>
            }
        >
            {loading || !session ? (
                <div className="sc-spinner"></div>
            ) : (
                <>
                    <div className="sc-stats">
                        <div className="sc-stat">
                            <div className="sc-stat-icon teal"><FontAwesomeIcon icon={faPlayCircle} /></div>
                            <div className="sc-stat-value">{session.status}</div>
                            <div className="sc-stat-label">Session status</div>
                        </div>
                        <div className="sc-stat">
                            <div className="sc-stat-icon blue"><FontAwesomeIcon icon={faClock} /></div>
                            <div className="sc-stat-value">{session.duration} min</div>
                            <div className="sc-stat-label">Duration</div>
                        </div>
                        <div className="sc-stat">
                            <div className="sc-stat-icon amber"><FontAwesomeIcon icon={faUserGroup} /></div>
                            <div className="sc-stat-value">{session.participants_count}</div>
                            <div className="sc-stat-label">Tracked participants</div>
                        </div>
                    </div>
                    <div className="sc-split">
                        <div>
                            <JitsiMeetingEmbed
                                roomName={session.room_name || `uzima-${session.session_id || session.id}`}
                                displayName={displayName}
                                email={email}
                                subject={session.title}
                                onClose={handleLeave}
                            />
                        </div>
                        <div className="sc-panel">
                            <div className="sc-panel-head">
                                <div>
                                    <h3>Session details</h3>
                                    <p>Reference information for the live consultation.</p>
                                </div>
                            </div>
                            <div className="sc-panel-body">
                                <div className="sc-list-stack">
                                    <div className="sc-list-item"><div><strong>Patient</strong><small style={{ display: "block", marginTop: 4 }}>{session.patient_name}</small></div></div>
                                    <div className="sc-list-item"><div><strong>Collaboration</strong><small style={{ display: "block", marginTop: 4 }}>{session.collaboration_title}</small></div></div>
                                    <div className="sc-list-item"><div><strong>Scheduled time</strong><small style={{ display: "block", marginTop: 4 }}>{new Date(session.scheduled_time).toLocaleString()}</small></div></div>
                                    <div className="sc-list-item"><div><strong>Meeting type</strong><small style={{ display: "block", marginTop: 4 }}>{session.session_type}</small></div></div>
                                    <div className="sc-list-item"><div><strong>Recording</strong><small style={{ display: "block", marginTop: 4 }}>{session.recording_enabled ? "Enabled for metadata" : "Disabled"}</small></div></div>
                                    {session.description && <div className="sc-list-item"><div><strong>Description</strong><small style={{ display: "block", marginTop: 4 }}>{session.description}</small></div></div>}
                                    {session.notes && <div className="sc-list-item"><div><strong>Notes</strong><small style={{ display: "block", marginTop: 4 }}>{session.notes}</small></div></div>}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </TelehealthShell>
    );
};

export default TelehealthSessionPage;
